import {DomEvent, DomEventHandlerObject, LngLat, YMap, YMapLocationRequest,} from "@yandex/ymaps3-types";
import {AddApiaryParams, useApiariesStore} from "entities/apiaries";
import {useUserStore} from "entities/user";
import {ChangeEvent, useContext, useEffect, useRef, useState} from "react";
import {ControllerRenderProps, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {DEFAULT_LOCATION, getReactify, getYmaps3React} from "shared/consts";
import {ApiaryType} from "shared/types";
import {ConfigContext} from "../../../app/App.tsx";

type CreateApiaryFormValues = {
  name: string;
  coordinates: string;
};

export const useCreateApiary = (
  onClose: () => void,
  edit?: boolean,
  currentApiary: ApiaryType | null = null
) => {
  const {VITE_YANDEX_TOKEN} = useContext(ConfigContext);
  
  const addApiary = useApiariesStore((s) => s.addApiary);
  const editApiary = useApiariesStore((s) => s.editApiary);
  const getApiaries = useApiariesStore((state) => state.getApiaries);
  const userInfo = useUserStore((s) => s.userInfo);

  const [coordinates, setCoordinates] = useState<LngLat | null>(null);
  const [isMapModalOpen, setIsMapModalOpen] = useState<boolean>(false);
  const [userCoords, setUserCoords] = useState<LngLat | null>(null);
  const [location, setLocation] =
    useState<YMapLocationRequest>(DEFAULT_LOCATION);

  const mapInstance = useRef<YMap | null>(null);
  const lastCoords = useRef<LngLat | null>(null);
  const ddRegex = /^-?\d+(\.\d+)?,\s-?\d+(\.\d+)?$/;

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
    setValue,
    watch,
  } = useForm<CreateApiaryFormValues>({
    defaultValues: {
      name: "",
      coordinates: "",
    },
  });
  
  const [reactify, setReactify] = useState<any>(null);
  
  const getMaps = async () => {
    const ymaps3React = await getYmaps3React()
    if (!ymaps3React) return null;
    const reactifyObject = await getReactify(ymaps3React)
    setReactify(reactifyObject)
  }
  
  useEffect(() => {
    if (VITE_YANDEX_TOKEN) getMaps()
  }, [VITE_YANDEX_TOKEN]);
  
  const formValues = watch();

  const onNameChange = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<CreateApiaryFormValues, "name">
  ) => {
    field.onChange(event);
  };

  const onNameReset = () => {
    setValue("name", "");
  };

  const isValidCoords = (coords: string) => {
    return ddRegex.test(coords);
  };

  const onCoordinatesChange = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<CreateApiaryFormValues, "coordinates">
  ) => {
    field.onChange(event);
    const newCoords = event.target.value;

    if (isValidCoords(newCoords)) {
      const newCoordsArr: LngLat = newCoords
        .split(", ")
        .map((el) => Number(el)) as LngLat;
      setCoordinates(newCoordsArr);
      lastCoords.current = newCoordsArr;
    }
  };

  const onCoordsFieldFocus = () => {
    if (coordinates) {
      setLocation((prevLoc) => ({
        ...prevLoc,
        center: coordinates,
        zoom: 12,
        duration: 500, 
      }));
    }
  
    else if (edit && currentApiary) {
      const mapCoords: LngLat = [
        Number(currentApiary.coordinates.longitude),
        Number(currentApiary.coordinates.latitude),
      ];
      setLocation((prevLoc) => ({
        ...prevLoc,
        center: mapCoords,
        zoom: 12,
        duration: 500, 
      }));
    }
  
    else if (userCoords) {
      setLocation((prevLoc) => ({
        ...prevLoc,
        center: userCoords,
        zoom: 12,
        duration: 500, 
      }));
    }

    setIsMapModalOpen(true);
  };

  const onMapModalClose = () => {
    setCoordinates(lastCoords.current);
    setIsMapModalOpen(false);
  };

  const onMapModalSave = () => {
    if (coordinates) {
      setValue("coordinates", coordinates?.join(", "));
      lastCoords.current = coordinates;
      setIsMapModalOpen(false);
    }
  };

  const onMapClick = (object: DomEventHandlerObject, event: DomEvent) => {

    const coords = event.coordinates;
    setCoordinates(coords);
    setLocation((prevLoc) => {
      return {
        ...prevLoc,
        center: coords,
        zoom: mapInstance.current?.zoom,
      };
    });
  };

  const onSubmit = async (data: CreateApiaryFormValues) => {
    const latitude = data.coordinates.split(", ")[1];
    const longitude = data.coordinates.split(", ")[0];
    const payload: AddApiaryParams = {
      name: data.name,
      latitude,
      longitude,
    };

    const res = await addApiary(payload);

    if (res) {
      toast.success("Пасека добавлена");
      if (window.ym) window.ym(111273960,'reachGoal','create_apiary')
    } else {
      toast.error("Произошла ошибка. Проверьте данные и попробуйте ещё раз");
    }

    onClose();
    reset();
    setCoordinates(null);
    lastCoords.current = null;
    setIsMapModalOpen(false);

    if (userInfo) {
      getApiaries(userInfo.id);
    }
  };

  const onSubmitEdit = async (data: CreateApiaryFormValues) => {
    const latitude = data.coordinates.split(", ")[1];
    const longitude = data.coordinates.split(", ")[0];
    const payload: AddApiaryParams = {
      name: data.name,
      latitude,
      longitude,
    };
    if (!currentApiary || !currentApiary.id) {
      toast.success("Не выбрана пасека для изменения");
      return;
    }

    const res = await editApiary(payload, currentApiary.id);

    if (res) {
      toast.success("Пасека изменена");
    } else {
      toast.error("Произошла ошибка. Проверьте данные и попробуйте ещё раз");
    }

    onClose();
    reset();
    setCoordinates(null);
    lastCoords.current = null;
    setIsMapModalOpen(false);

    if (userInfo) {
      getApiaries(userInfo.id);
    }
  };

  useEffect(() => {
    if (!userCoords && reactify) {
      reactify.geolocation
        .getPosition()
        .then((res: any) => {
          setUserCoords(res.coords);
          setLocation((prevLoc) => {
            return {
              ...prevLoc,
              center: res.coords,
            };
          });
        })
        .catch(console.warn);
    }
  }, []);

  useEffect(() => {
    if (edit && currentApiary) {
      const coordsString = `${currentApiary.coordinates.longitude}, ${currentApiary.coordinates.latitude}`;
      const mapCoords: LngLat = [
        Number(currentApiary.coordinates.longitude),
        Number(currentApiary.coordinates.latitude),
      ];

      setValue("name", currentApiary.name);
      setValue("coordinates", coordsString);
      setCoordinates(mapCoords);
      lastCoords.current = mapCoords;
      setLocation({
        center: mapCoords,
        zoom: 12,
      });
    }
  }, [edit, currentApiary, setValue]);

  return {
    control,
    errors,
    coordinates,
    isMapModalOpen,
    location,
    mapInstance,
    formValues,
    userCoords,
    handleSubmit,
    onSubmit,
    onSubmitEdit,
    reset,
    setValue,
    onNameChange,
    onNameReset,
    onCoordsFieldFocus,
    onMapModalClose,
    onMapModalSave,
    onMapClick,
    onCoordinatesChange,
    setUserCoords,
  };
};
